const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function getPageFocusable(): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => el.offsetParent !== null
  );
}

/** Focus the next focusable element on the page after `anchor`. */
export function focusNextAfter(anchor: HTMLElement): void {
  const all = getPageFocusable();
  const idx = all.indexOf(anchor);
  all[idx + 1]?.focus();
}

/** Focus the previous focusable element on the page before `anchor`. */
export function focusPrevBefore(anchor: HTMLElement): void {
  const all = getPageFocusable();
  const idx = all.indexOf(anchor);
  all[idx - 1]?.focus();
}

/**
 * Keyboard handler for a menu content element.
 * - Tab: navigate to next item, or exit menu and continue on page.
 * - Shift+Tab: navigate to previous item, or exit menu and return to trigger.
 * - Other keys are ignored (Ark UI handles Arrow/Enter/Escape natively).
 */
export function createMenuContentKeyHandler(opts: {
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  closeMenu: () => void;
}): (e: React.KeyboardEvent<HTMLElement>) => void {
  return (e) => {
    if (e.key !== 'Tab') return;

    const items = Array.from(
      e.currentTarget.querySelectorAll<HTMLElement>(
        '[data-scope="menu"][data-part="item"]:not([aria-disabled="true"]), [role^="menuitem"]:not([aria-disabled="true"])'
      )
    );
    if (!items.length) return;

    const idx = items.findIndex((el) => el === document.activeElement);

    if (!e.shiftKey && idx === items.length - 1) {
      // Last item + Tab → close menu, advance to next element after trigger
      e.preventDefault();
      opts.closeMenu();
      if (opts.triggerRef.current) focusNextAfter(opts.triggerRef.current);
      return;
    }

    if (e.shiftKey && idx === 0) {
      // First item + Shift+Tab → close menu, return to trigger
      e.preventDefault();
      opts.closeMenu();
      opts.triggerRef.current?.focus();
      return;
    }

    // Mid-list navigation
    e.preventDefault();
    items[e.shiftKey ? idx - 1 : idx + 1]?.focus();
  };
}

/**
 * Keyboard handler for the trigger button.
 * When the menu is open and Tab is pressed, enters the menu (focuses first item).
 */
export function createTriggerKeyHandler(opts: {
  isOpen: boolean;
  contentRef: React.RefObject<HTMLDivElement | null>;
}): (e: React.KeyboardEvent) => void {
  return (e) => {
    if (e.key === 'Tab' && opts.isOpen) {
      e.preventDefault();
      opts.contentRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    }
  };
}
